import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';
import {
  Plus, Search, ChevronDown, X, Trash2, Loader2, AlertTriangle,
  Zap, ClipboardList, Wrench, ThumbsUp, Play, FileCheck,
  CheckCircle, Clock, RefreshCw, Users, BarChart2, Filter, CircleDot,
  Columns, Check, Eye, MessageSquare, CheckSquare, FileSearch,
  ShieldCheck, ShieldX, ClipboardCheck, ArrowUp, ArrowDown, GripVertical,
  Download,
} from 'lucide-react';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { isControlUuid } from '../features/controls/functional.ts';
import { TENANT_PATHS } from '../routing/paths.ts';
import CreateIssueModal from './issues/CreateIssueModal';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { MODULE_KEYS } from '../constants/moduleAccess.ts';
import ModuleAuditDownloadButton from './shared/ModuleAuditDownloadButton.tsx';

// ─── Status pipeline ─────────────────────────────────────────────────────────

const STATUS_PIPELINE = [
  { key: 'draft',                    label: 'Draft',                    icon: Clock,         color: 'text-red-500'      },
  { key: 'reviewed',                 label: 'Team Manager Review',       icon: ClipboardCheck, color: 'text-sky-500'     },
  { key: 'in_management_review',     label: 'Mgmt Review',              icon: Eye,           color: 'text-blue-500'     },
  { key: 'in_discussion',            label: 'In Discussion',            icon: MessageSquare, color: 'text-amber-500'    },
  { key: 'rework',                   label: 'In Rework',                icon: RefreshCw,     color: 'text-orange-500'   },
  { key: 'plan_accepted',            label: 'Plan Accepted',            icon: ThumbsUp,      color: 'text-green-500'    },
  { key: 'remediation_in_progress',  label: 'Remediation In Progress',  icon: Wrench,        color: 'text-orange-600'   },
  { key: 'remediation_complete',     label: 'Remediation Complete',     icon: CheckSquare,   color: 'text-blue-600'     },
  { key: 'pending_verification',     label: 'Pending Verification',     icon: FileSearch,    color: 'text-indigo-500'   },
  { key: 'verification_complete',    label: 'Verification Complete',    icon: ShieldCheck,   color: 'text-emerald-500'  },
  { key: 'verification_failed',      label: 'Verification Failed',      icon: ShieldX,       color: 'text-red-600'      },
  { key: 'closed',                   label: 'Closed',                   icon: CheckCircle,   color: 'text-gray-400'     },
  { key: 'reopened',                 label: 'Reopened',                 icon: RefreshCw,     color: 'text-amber-500'    },
  { key: 'escalated',                label: 'Escalated',                icon: Zap,           color: 'text-purple-500'   },
];

// Grouped cards — 8 buckets aligned with the pipeline stepper
const PIPELINE_CARDS = [
  { key: 'draft',         label: 'Draft',        icon: Clock,         color: 'text-red-500',    statuses: ['draft']                                                              },
  { key: 'team_review',   label: 'Team Manager Review', icon: ClipboardCheck,color: 'text-sky-500',    statuses: ['reviewed']                                                           },
  { key: 'rework',        label: 'Rework',       icon: RefreshCw,     color: 'text-orange-500', statuses: ['rework']                                                             },
  { key: 'mgmt_review',   label: 'Mgmt Review',  icon: Eye,           color: 'text-blue-500',   statuses: ['in_management_review', 'in_discussion']                              },
  { key: 'remediation',   label: 'Remediation',  icon: Wrench,        color: 'text-green-600',  statuses: ['plan_accepted', 'remediation_in_progress', 'remediation_complete']   },
  { key: 'verification',  label: 'Verification', icon: ShieldCheck,   color: 'text-indigo-500', statuses: ['pending_verification', 'verification_complete', 'verification_failed']},
  { key: 'closed',        label: 'Closed',       icon: CheckCircle,   color: 'text-gray-400',   statuses: ['closed']                                                             },
  { key: 'reopened',      label: 'Reopened',     icon: RefreshCw,     color: 'text-amber-500',  statuses: ['reopened']                                                           },
  { key: 'escalated',     label: 'Escalated',    icon: Zap,           color: 'text-purple-500', statuses: [],  isFlag: true                                                      },
];

// Maps a group key to its constituent status keys (for filter matching)
const GROUP_STATUSES = Object.fromEntries(PIPELINE_CARDS.filter(c => !c.isFlag).map(c => [c.key, c.statuses]));

const PIE_BUCKET_COLORS = {
  draft:        '#ef4444',
  team_review:  '#0ea5e9',
  rework:       '#f97316',
  mgmt_review:  '#3b82f6',
  remediation:  '#22c55e',
  verification: '#6366f1',
  closed:       '#9ca3af',
  reopened:     '#f59e0b',
  escalated:    '#a855f7',
};

const STATUS_LABELS = {
  draft:                   'Draft',
  reviewed:                'Team Manager Review',
  in_management_review:    'Mgmt Review',
  in_discussion:           'In Discussion',
  rework:                  'In Rework',
  plan_accepted:           'Plan Accepted',
  remediation_in_progress: 'Remediation In Progress',
  remediation_complete:    'Remediation Complete',
  pending_verification:    'Pending Verification',
  verification_complete:   'Verification Complete',
  verification_failed:     'Verification Failed',
  closed:                  'Closed',
  reopened:                'Reopened',
  escalated:               'Escalated',
};

const STATUS_BADGE_COLORS = {
  draft:                   'text-red-600',
  reviewed:                'text-sky-600',
  in_management_review:    'text-blue-600',
  in_discussion:           'text-amber-600',
  rework:                  'text-orange-600',
  plan_accepted:           'text-green-600',
  remediation_in_progress: 'text-orange-700',
  remediation_complete:    'text-blue-700',
  pending_verification:    'text-indigo-600',
  verification_complete:   'text-emerald-600',
  verification_failed:     'text-red-600',
  closed:                  'text-gray-500',
  reopened:                'text-amber-600',
  escalated:               'text-purple-600',
};

// ─── Classification ───────────────────────────────────────────────────────────

const SEVERITY_CONFIG = {
  observation:           { short: 'OBS', label: 'Observation',            bg: 'bg-green-500',  text: 'text-white' },
  material_weakness:     { short: 'MW',  label: 'Material Weakness',      bg: 'bg-red-500',    text: 'text-white' },
  significant_deficiency:{ short: 'SD',  label: 'Significant Deficiency', bg: 'bg-yellow-500', text: 'text-white' },
  control_deficiency:    { short: 'CD',  label: 'Control Deficiency',     bg: 'bg-blue-600',   text: 'text-white' },
};

function SeverityBadge({ value }) {
  if (!value) return <span className="text-gray-400 text-xs">—</span>;
  const cfg = SEVERITY_CONFIG[value];
  if (!cfg) return <span className="text-xs text-gray-500">{value}</span>;
  return (
    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${cfg.bg} ${cfg.text}`}>
      {cfg.short}
    </span>
  );
}

function StatusCell({ value }) {
  const StageIcon = STATUS_PIPELINE.find(s => s.key === value)?.icon || Clock;
  const color = STATUS_BADGE_COLORS[value] || 'text-gray-500';
  const label = STATUS_LABELS[value] || value;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`}>
      <StageIcon className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}

// ─── Column definitions ───────────────────────────────────────────────────────

const ALL_COLUMNS = [
  { key: 'id',         label: 'ID',         defaultOn: true  },
  { key: 'title',      label: 'Title',      defaultOn: true  },
  { key: 'status',     label: 'Status',     defaultOn: true  },
  { key: 'severity',   label: 'Severity',   defaultOn: true  },
  { key: 'category',   label: 'Category',   defaultOn: true  },
  { key: 'issue_type', label: 'Issue Type', defaultOn: true  },
  { key: 'control_id', label: 'Control ID', defaultOn: true  },
  { key: 'recurring',  label: 'Recurring',  defaultOn: true  },
  { key: 'department', label: 'Department', defaultOn: false },
  { key: 'assignee',   label: 'Assignee',   defaultOn: false },
  { key: 'period',     label: 'Period',     defaultOn: false },
];
const DEFAULT_COLUMNS = ALL_COLUMNS.filter(c => c.defaultOn).map(c => c.key);
const PREFS_KEY = 'issues_columns';

// ─── Period options ────────────────────────────────────────────────────────────

function buildPeriodOptions() {
  const options = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  for (let y = currentYear - 2; y <= currentYear + 1; y++) {
    options.push(`FY ${y}`);
    for (let q = 1; q <= 4; q++) {
      options.push(`Q${q} ${y}`);
    }
  }
  return options;
}
const PERIOD_OPTIONS = buildPeriodOptions();

// ─── Filter definitions ───────────────────────────────────────────────────────

const FILTERABLE_COLS = [
  { key: 'status',     label: 'Status'     },
  { key: 'severity',   label: 'Severity'   },
  { key: 'category',   label: 'Category'   },
  { key: 'issue_type', label: 'Issue Type' },
  { key: 'department', label: 'Department' },
  { key: 'period',     label: 'Period'     },
  { key: 'recurring',  label: 'Recurring'  },
  { key: 'assignee',   label: 'Assignee'   },
  { key: 'control_id', label: 'Control ID' },
];

function chipValueLabel(col, value) {
  if (col === 'status')     return STATUS_LABELS[value] || value;
  if (col === 'severity')   return SEVERITY_CONFIG[value]?.label || value;
  if (col === 'recurring')  return value === 'yes' ? 'Yes' : 'No';
  if (col === 'issue_type') return value === 'design' ? 'Design Deficiency' : 'Operating Effectiveness';
  return value;
}

// ─── Form constants ───────────────────────────────────────────────────────────

// TODO: Department list should come from a DB table (follow-up item).
const DEPARTMENTS = ['Finance', 'HR', 'IT', 'Operations', 'Accounting', 'Legal', 'Procurement', 'Sales', 'Inventory'];

const BASE_CATEGORIES = ['Authorization', 'Access Management', 'Segregation of Duties', 'Financial Reporting', 'Change Management', 'Vendor Management', 'Physical Security', 'Data Integrity', 'Documentation'];
const CUSTOM_CATEGORIES_KEY = 'sox_issue_custom_categories';

const RUN_STATUS_BADGE = {
  failed:  { label: 'Failed',  cls: 'bg-red-100 text-red-700'    },
  warning: { label: 'Warning', cls: 'bg-amber-100 text-amber-700' },
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function IssuesManagement() {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { selectedAuditYear, selectedAuditYearId } = useAuditYear();
  const isLocked = Boolean(selectedAuditYear?.is_locked);
  const colPrefsDebounceRef = useRef(null);
  const [issues, setIssues]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [controls, setControls] = useState([]);

  // Column chooser
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_COLUMNS);
  const [showColMenu, setShowColMenu] = useState(false);
  const [colPrefsLoading, setColPrefsLoading] = useState(true);
  const colMenuRef = useRef(null);

  // Sort
  const [sortBy,  setSortBy]  = useState(null); // 'id' | 'title'
  const [sortDir, setSortDir] = useState('asc');

  // Column drag-to-reorder
  const [dragCol,     setDragCol]     = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  // Active filters (replaces filterDepartment / filterStatus)
  const [activeFilters, setActiveFilters] = useState([]);

  // Filter picker popover
  const [filterPickerOpen, setFilterPickerOpen] = useState(false);
  const [filterPickerCol,  setFilterPickerCol]  = useState(null); // null = step 1, string = step 2
  const [filterPickerText, setFilterPickerText] = useState('');
  const filterPickerRef = useRef(null);

  // Categories (base + user-added, persisted to localStorage)
  const [categories, setCategories] = useState(() => {
    try {
      const custom = JSON.parse(localStorage.getItem(CUSTOM_CATEGORIES_KEY) || '[]');
      const all = [...BASE_CATEGORIES];
      custom.forEach(c => { if (!all.includes(c)) all.push(c); });
      return all;
    } catch { return BASE_CATEGORIES; }
  });
  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Status pie chart period filter
  const [pieStatusPeriod, setPieStatusPeriod] = useState('');

  // Create modal
  const [showCreate,  setShowCreate]  = useState(false);

  // Delete modal
  const [deleteModal, setDeleteModal] = useState({ open: false, issue: null });
  const [deleting,    setDeleting]    = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Declare fetchIssues before the useEffect that references it (avoids TDZ error)
  const fetchIssues = useCallback(async () => {
    try {
      const params = selectedAuditYearId ? { audit_year_id: selectedAuditYearId } : {};
      const res = await axiosInstance.get('/api/sox-issues', { params });
      setIssues(res.data.issues || []);
    } catch (e) { console.error(e); setIssues([]); }
    finally { setLoading(false); }
  }, [selectedAuditYearId]);

  useEffect(() => { fetchIssues(); fetchControls(); loadColPrefs(); }, [fetchIssues]);
  useEffect(() => () => { if (colPrefsDebounceRef.current) clearTimeout(colPrefsDebounceRef.current); }, []);

  // Close column menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target)) setShowColMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close filter picker on outside click
  useEffect(() => {
    const handler = (e) => {
      if (filterPickerRef.current && !filterPickerRef.current.contains(e.target)) {
        setFilterPickerOpen(false);
        setFilterPickerCol(null);
        setFilterPickerText('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openCreateModal = () => { setShowCreate(true); };

  const fetchControls = async () => {
    try {
      const res = await axiosInstance.get('/api/sox-controls');
      setControls(res?.data?.controls || []);
    } catch (e) { console.error(e); }
  };

  // ── Column preferences ──────────────────────────────────────────────────────

  const loadColPrefs = async () => {
    try {
      const res = await axiosInstance.get('/api/user-preferences');
      const prefs = res.data?.preferences;
      if (prefs?.[PREFS_KEY]?.length) setVisibleColumns(prefs[PREFS_KEY]);
    } catch (e) { /* use defaults */ }
    finally { setColPrefsLoading(false); }
  };

  const saveColPrefs = useCallback((cols) => {
    if (colPrefsDebounceRef.current) clearTimeout(colPrefsDebounceRef.current);
    colPrefsDebounceRef.current = setTimeout(async () => {
      try {
        const res = await axiosInstance.get('/api/user-preferences');
        const existing = res.data?.preferences || {};
        await axiosInstance.put('/api/user-preferences', {
          preferences: { ...existing, [PREFS_KEY]: cols },
        });
      } catch (e) { console.error('[saveColPrefs]', e); }
    }, 300);
  }, []);

  const toggleColumn = (key) => {
    setVisibleColumns(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      saveColPrefs(next);
      return next;
    });
  };

  // ── Filter helpers ──────────────────────────────────────────────────────────

  // toggleFilter adds or removes a single value from a column's filter set.
  const toggleFilter = useCallback((colKey, value) => {
    setActiveFilters(prev => {
      const existing = prev.find(f => f.col === colKey);
      if (!existing) return [...prev, { col: colKey, values: [value] }];
      const newValues = existing.values.includes(value)
        ? existing.values.filter(v => v !== value)
        : [...existing.values, value];
      return newValues.length === 0
        ? prev.filter(f => f.col !== colKey)
        : prev.map(f => f.col === colKey ? { ...f, values: newValues } : f);
    });
  }, []);

  // addFilter sets exactly one value for a column (used by header buttons).
  const addFilter = useCallback((colKey, value) => {
    setActiveFilters(prev => {
      const existing = prev.find(f => f.col === colKey);
      if (existing?.values.includes(value)) return prev;
      return [...prev.filter(f => f.col !== colKey), { col: colKey, values: [value] }];
    });
  }, []);

  const removeFilter = useCallback((colKey) => {
    setActiveFilters(prev => prev.filter(f => f.col !== colKey));
  }, []);

  // ── Sort helper ─────────────────────────────────────────────────────────────

  const handleSort = useCallback((key) => {
    if (sortBy === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  }, [sortBy]);

  // ── Column drag-to-reorder ──────────────────────────────────────────────────

  const handleColDragStart = (e, key) => {
    setDragCol(key);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleColDragOver = (e, key) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (key !== dragOverCol) setDragOverCol(key);
  };
  const handleColDrop = (e, targetKey) => {
    e.preventDefault();
    if (!dragCol || dragCol === targetKey) { setDragCol(null); setDragOverCol(null); return; }
    setVisibleColumns(prev => {
      const next = [...prev];
      const from = next.indexOf(dragCol);
      const to   = next.indexOf(targetKey);
      if (from === -1 || to === -1) return prev;
      next.splice(from, 1);
      next.splice(to, 0, dragCol);
      saveColPrefs(next);
      return next;
    });
    setDragCol(null);
    setDragOverCol(null);
  };
  const handleColDragEnd = () => { setDragCol(null); setDragOverCol(null); };

  // ── Derived stats ───────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const statusCounts = {};
    STATUS_PIPELINE.forEach(s => { statusCounts[s.key] = 0; });
    const deptCounts = {};
    const severityCounts = { observation: 0, material_weakness: 0, significant_deficiency: 0, control_deficiency: 0 };
    let recurring = 0, escalated = 0;

    issues.forEach(issue => {
      if (statusCounts[issue.status] !== undefined) statusCounts[issue.status]++;
      if (issue.department) deptCounts[issue.department] = (deptCounts[issue.department] || 0) + 1;
      if (issue.classification && severityCounts[issue.classification] !== undefined) severityCounts[issue.classification]++;
      if (issue.is_repeat) recurring++;
      if (issue.is_escalated) escalated++;
    });

    const groupCounts = {};
    PIPELINE_CARDS.forEach(card => {
      if (card.isFlag) {
        groupCounts[card.key] = issues.filter(i => i.is_escalated).length;
      } else {
        groupCounts[card.key] = card.statuses.reduce((sum, s) => sum + (statusCounts[s] || 0), 0);
      }
    });

    const deptList = Object.entries(deptCounts).sort((a, b) => b[1] - a[1]);
    const maxDept = deptList[0]?.[1] || 1;
    return { statusCounts, groupCounts, deptList, maxDept, severityCounts, recurring, escalated };
  }, [issues]);

  // ── Status pie chart data ───────────────────────────────────────────────────

  const pieUniquePeriods = useMemo(() =>
    [...new Set(issues.map(i => i.period).filter(Boolean))].sort(),
  [issues]);

  const pieStatusData = useMemo(() => {
    const subset = pieStatusPeriod ? issues.filter(i => i.period === pieStatusPeriod) : issues;
    return PIPELINE_CARDS
      .map(card => {
        const count = card.isFlag
          ? subset.filter(i => i.is_escalated).length
          : card.statuses.reduce((s, st) => s + subset.filter(i => i.status === st).length, 0);
        return { key: card.key, label: card.label, count, color: PIE_BUCKET_COLORS[card.key] };
      })
      .filter(d => d.count > 0);
  }, [issues, pieStatusPeriod]);

  // ── Filtered + sorted table ─────────────────────────────────────────────────

  const filteredIssues = useMemo(() => {
    const q = searchQuery.toLowerCase();
    let result = issues.filter(issue => {
      const controlIds = (issue.controls || []).map(c => c.control_id).join(' ').toLowerCase();
      const matchSearch = !q ||
        issue.issue_id?.toLowerCase().includes(q) ||
        issue.title?.toLowerCase().includes(q) ||
        issue.department?.toLowerCase().includes(q) ||
        issue.category?.toLowerCase().includes(q) ||
        controlIds.includes(q);

      const matchFilters = activeFilters.every(({ col: fc, values }) => {
        return values.some(value => {
          switch (fc) {
            case 'status':
              if (value === 'escalated') return issue.is_escalated;
              if (GROUP_STATUSES[value]) return GROUP_STATUSES[value].includes(issue.status);
              return issue.status === value;
            case 'severity':   return issue.classification === value;
            case 'category':   return issue.category === value;
            case 'issue_type': return (issue.issue_type || 'operating_effectiveness') === value;
            case 'department': return issue.department === value;
            case 'period':     return issue.period === value;
            case 'recurring':  return value === 'yes' ? issue.is_repeat : !issue.is_repeat;
            case 'assignee':   return (issue.assignee_name || '').toLowerCase().includes(value.toLowerCase());
            case 'control_id': return (issue.controls || []).some(c => c.control_id.toLowerCase().includes(value.toLowerCase()));
            default:           return true;
          }
        });
      });

      return matchSearch && matchFilters;
    });

    if (sortBy) {
      result = [...result].sort((a, b) => {
        if (sortBy === 'id') {
          const aVal = a.issue_year * 10000 + a.issue_number;
          const bVal = b.issue_year * 10000 + b.issue_number;
          return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
        }
        const aVal = (a.title || '').toLowerCase();
        const bVal = (b.title || '').toLowerCase();
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      });
    }

    return result;
  }, [issues, searchQuery, activeFilters, sortBy, sortDir]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleRowClick = (issue) =>
    navigate(TENANT_PATHS.ISSUE_DETAIL.replace(':id', issue.id), { state: { issue } });

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDeleteConfirm = async () => {
    if (!deleteModal.issue) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await axiosInstance.delete(`/api/sox-issues/${deleteModal.issue.id}`);
      setIssues(prev => prev.filter(i => i.id !== deleteModal.issue.id));
      setDeleteModal({ open: false, issue: null });
    } catch (e) {
      console.error(e);
      setDeleteError(resolve403ErrorMessage(e, 'Failed to delete issue. Please try again.'));
    } finally { setDeleting(false); }
  };

  const uniqueDepartments = [...new Set(issues.map(i => i.department).filter(Boolean))].sort();

  // ── Export ─────────────────────────────────────────────────────────────────

  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) setShowExportMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const buildExportData = useCallback(() => {
    const headers = visibleColumns.map(key => ALL_COLUMNS.find(c => c.key === key)?.label || key);
    const rows = filteredIssues.map(issue =>
      visibleColumns.map(key => {
        switch (key) {
          case 'id':         return issue.issue_id || '';
          case 'title':      return issue.title || '';
          case 'status':     return STATUS_LABELS[issue.status] || issue.status || '';
          case 'severity':   return SEVERITY_CONFIG[issue.classification]?.label || issue.classification || '';
          case 'category':   return issue.category || '';
          case 'issue_type': return issue.issue_type || 'operating_effectiveness';
          case 'control_id': return (issue.controls || []).map(c => c.control_id).join(', ');
          case 'department': return issue.department || '';
          case 'assignee':   return issue.assignee_name || '';
          case 'period':     return issue.period || '';
          case 'recurring':  return issue.is_repeat ? 'Yes' : 'No';
          default:           return '';
        }
      })
    );
    return { headers, rows };
  }, [visibleColumns, filteredIssues]);

  const exportCSV = useCallback(() => {
    const { headers, rows } = buildExportData();
    const escape = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map(row => row.map(escape).join(',')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sox_issues_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  }, [buildExportData]);

  const exportExcel = useCallback(async () => {
    const { headers, rows } = buildExportData();
    const XLSX = await import('xlsx');
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    // Auto column widths
    ws['!cols'] = headers.map((h, i) => ({
      wch: Math.max(h.length, ...rows.map(r => String(r[i] || '').length), 10),
    }));
    // Freeze header row
    ws['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft', state: 'frozen' };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SOX Issues');
    XLSX.writeFile(wb, `sox_issues_${new Date().toISOString().slice(0, 10)}.xlsx`);
    setShowExportMenu(false);
  }, [buildExportData]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        <ReadOnlyBanner />

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Issues Management</h1>
            <p className="text-sm text-gray-500 mt-1">Track and triage control deficiencies and remediation.</p>
            <div className="flex items-center gap-2 mt-3">
              {stats.escalated > 0 && (
                <button
                  onClick={() => {
                    const active = activeFilters.some(f => f.col === 'status' && f.values?.includes('escalated'));
                    active ? removeFilter('status') : addFilter('status', 'escalated');
                  }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${activeFilters.some(f => f.col === 'status' && f.values?.includes('escalated')) ? 'bg-purple-100 text-purple-800 border-purple-400' : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'}`}
                >
                  <Zap className="h-3.5 w-3.5" />{stats.escalated} Escalated
                </button>
              )}
              {stats.recurring > 0 && (
                <button
                  onClick={() => {
                    const active = activeFilters.some(f => f.col === 'recurring' && f.values?.includes('yes'));
                    active ? removeFilter('recurring') : addFilter('recurring', 'yes');
                  }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${activeFilters.some(f => f.col === 'recurring' && f.values?.includes('yes')) ? 'bg-amber-100 text-amber-800 border-amber-400' : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'}`}
                >
                  <RefreshCw className="h-3.5 w-3.5" />{stats.recurring} Recurring
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ModuleAuditDownloadButton
              resourceType="sox_issue"
              moduleKey={MODULE_KEYS.SOX_ISSUES}
              filenamePrefix="sox_issue_audit"
            />
            <button
              onClick={openCreateModal}
              disabled={isLocked}
              title={isLocked ? 'Audit year is locked' : undefined}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus className="h-4 w-4" />Create Issue
            </button>
          </div>
        </div>

        {/* Status pipeline cards */}
        <div className="grid grid-cols-9 gap-2">
          {PIPELINE_CARDS.map(({ key, label, icon: Icon, color }) => (
            <button
              key={key}
              onClick={() => toggleFilter('status', key)}
              className={`bg-white rounded-xl border p-2 text-center transition-all hover:shadow-md ${activeFilters.some(f => f.col === 'status' && f.values?.includes(key)) ? 'border-blue-400 shadow-md' : 'border-gray-200'}`}
            >
              <Icon className={`h-4 w-4 mx-auto mb-1 ${color}`} />
              <p className="text-lg font-bold text-gray-900">{stats.groupCounts[key] || 0}</p>
              <p className="text-xs text-gray-500 leading-tight mt-0.5">{label}</p>
            </button>
          ))}
        </div>

        {/* Analytics row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-4 w-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-800">Issues by Department</h3>
            </div>
            {stats.deptList.length === 0 ? (
              <p className="text-xs text-gray-400 py-4 text-center">No data yet</p>
            ) : (
              <div className="space-y-3">
                {stats.deptList.slice(0, 6).map(([dept, count]) => (
                  <div key={dept} className="flex items-center gap-3">
                    <span className="text-sm text-gray-700 w-24 shrink-0 truncate">{dept}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-2">
                      <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${(count / stats.maxDept) * 100}%` }} />
                    </div>
                    <span className="text-sm font-medium text-gray-700 w-4 text-right">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart2 className="h-4 w-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-800">SOX Severity Distribution</h3>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(SEVERITY_CONFIG).map(([key, cfg]) => {
                const isActive = activeFilters.some(f => f.col === 'severity' && f.values?.includes(key));
                return (
                  <button
                    key={key}
                    onClick={() => toggleFilter('severity', key)}
                    className={`flex flex-col items-center justify-center rounded-lg p-2 gap-1 transition-all hover:shadow-md border ${isActive ? 'bg-white border-blue-400 shadow-md' : 'bg-gray-50 border-transparent'}`}
                  >
                    <span className="text-xl font-bold text-gray-900">{stats.severityCounts[key] || 0}</span>
                    <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold ${cfg.bg} ${cfg.text}`}>{cfg.short}</span>
                    <span className="text-xs text-gray-500 text-center leading-tight">{cfg.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Issues by Status pie chart */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CircleDot className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-800">Issues by Period</h3>
              </div>
              {pieUniquePeriods.length > 0 && (
                <div className="relative">
                  <select
                    value={pieStatusPeriod}
                    onChange={e => setPieStatusPeriod(e.target.value)}
                    className="appearance-none text-xs text-gray-600 border border-gray-200 rounded-lg pl-2 pr-6 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All periods</option>
                    {pieUniquePeriods.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                </div>
              )}
            </div>
            {pieStatusData.length === 0 ? (
              <p className="text-xs text-gray-400 py-8 text-center">No data yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={pieStatusData}
                    dataKey="count"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={2}
                    onClick={d => {
                      const card = PIPELINE_CARDS.find(c => c.key === d.key);
                      if (!card || card.isFlag) return;
                      // Group toggle: if any of this card's statuses are active, clear them all;
                      // otherwise add them all. Avoids inverted selection from per-status toggles.
                      setActiveFilters(prev => {
                        const cur = prev.find(f => f.col === 'status');
                        const anyActive = card.statuses.some(s => cur?.values?.includes(s));
                        if (anyActive) {
                          const remaining = (cur?.values || []).filter(s => !card.statuses.includes(s));
                          return remaining.length === 0
                            ? prev.filter(f => f.col !== 'status')
                            : prev.map(f => f.col === 'status' ? { ...f, values: remaining } : f);
                        }
                        if (cur) return prev.map(f => f.col === 'status' ? { ...f, values: [...new Set([...f.values, ...card.statuses])] } : f);
                        return [...prev, { col: 'status', values: [...card.statuses] }];
                      });
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {pieStatusData.map(entry => (
                      <Cell key={entry.key} fill={entry.color} stroke="none" />
                    ))}
                  </Pie>
                  <text
                    x="50%" y="44%" textAnchor="middle" dominantBaseline="middle"
                    className="fill-gray-900" style={{ fontSize: 22, fontWeight: 700, fill: '#111827' }}
                  >
                    {pieStatusData.reduce((s, d) => s + d.count, 0)}
                  </text>
                  <text
                    x="50%" y="56%" textAnchor="middle" dominantBaseline="middle"
                    style={{ fontSize: 10, fill: '#9ca3af' }}
                  >
                    total
                  </text>
                  <Tooltip
                    formatter={(value, name) => [value, name]}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    formatter={(value) => <span style={{ fontSize: 11, color: '#6b7280' }}>{value}</span>}
                    wrapperStyle={{ paddingTop: 8 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Table section */}
        <div className="bg-white rounded-xl border border-gray-200">
          {/* Search + Filters + Column chooser */}
          <div className="p-4 border-b border-gray-100 space-y-2">
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search issues..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* + Filter picker */}
              <div className="relative" ref={filterPickerRef}>
                <button
                  onClick={() => { setFilterPickerOpen(v => !v); setFilterPickerCol(null); setFilterPickerText(''); }}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <Filter className="h-4 w-4" />+ Filter
                </button>
                {filterPickerOpen && (
                  <div className="absolute left-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-xl shadow-lg w-52 py-1">
                    {filterPickerCol === null ? (
                      FILTERABLE_COLS.map(fc => {
                        const af = activeFilters.find(f => f.col === fc.key);
                        return (
                          <button
                            key={fc.key}
                            onClick={() => { setFilterPickerCol(fc.key); setFilterPickerText(''); }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between"
                          >
                            <span>{fc.label}</span>
                            {af && <span className="text-xs text-blue-600 font-medium">{af.values.length}</span>}
                          </button>
                        );
                      })
                    ) : (() => {
                      const af = activeFilters.find(f => f.col === filterPickerCol);
                      const selected = af?.values || [];
                      const isSelected = (v) => selected.includes(v);
                      const backBtn = (
                        <button onClick={() => setFilterPickerCol(null)} className="w-full text-left px-3 py-2 text-xs text-gray-400 hover:bg-gray-50 flex items-center gap-1">
                          <ChevronDown className="h-3 w-3 rotate-90" />Back
                        </button>
                      );
                      const optionBtn = (v, label) => (
                        <button key={v} onClick={() => toggleFilter(filterPickerCol, v)}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 ${isSelected(v) ? 'text-blue-600 font-medium bg-blue-50/60' : 'text-gray-700'}`}>
                          <span className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center ${isSelected(v) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                            {isSelected(v) && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                          </span>
                          <span>{label}</span>
                        </button>
                      );
                      if (filterPickerCol === 'status') return (
                        <><div>{backBtn}</div><div className="max-h-56 overflow-y-auto">{STATUS_PIPELINE.map(s => optionBtn(s.key, s.label))}</div></>
                      );
                      if (filterPickerCol === 'severity') return (
                        <><div>{backBtn}</div>{Object.entries(SEVERITY_CONFIG).map(([k, cfg]) => optionBtn(k, cfg.label))}</>
                      );
                      if (filterPickerCol === 'category') return (
                        <><div>{backBtn}</div><div className="max-h-56 overflow-y-auto">{categories.map(c => optionBtn(c, c))}</div></>
                      );
                      if (filterPickerCol === 'department') return (
                        <><div>{backBtn}</div>{uniqueDepartments.length === 0 ? <p className="px-3 py-2 text-xs text-gray-400">No departments</p> : uniqueDepartments.map(d => optionBtn(d, d))}</>
                      );
                      if (filterPickerCol === 'period') return (
                        <><div>{backBtn}</div><div className="max-h-56 overflow-y-auto">{PERIOD_OPTIONS.map(p => optionBtn(p, p))}</div></>
                      );
                      if (filterPickerCol === 'recurring') return (
                        <><div>{backBtn}</div>{[{ v: 'yes', label: 'Yes' }, { v: 'no', label: 'No' }].map(({ v, label }) => optionBtn(v, label))}</>
                      );
                      if (filterPickerCol === 'issue_type') return (
                        <><div>{backBtn}</div>{[{ v: 'operating_effectiveness', label: 'Operating Effectiveness' }, { v: 'design', label: 'Design Deficiency' }].map(({ v, label }) => optionBtn(v, label))}</>
                      );
                      // Text: assignee / control_id
                      return (
                        <>
                          <div>{backBtn}</div>
                          <div className="px-3 py-2 space-y-2">
                            <input
                              autoFocus
                              type="text"
                              value={filterPickerText}
                              onChange={e => setFilterPickerText(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter' && filterPickerText.trim()) { toggleFilter(filterPickerCol, filterPickerText.trim()); setFilterPickerText(''); } }}
                              placeholder={`Filter by ${FILTERABLE_COLS.find(c => c.key === filterPickerCol)?.label}…`}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                              onClick={() => { if (filterPickerText.trim()) { toggleFilter(filterPickerCol, filterPickerText.trim()); setFilterPickerText(''); } }}
                              disabled={!filterPickerText.trim()}
                              className="w-full px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 transition-colors"
                            >Add</button>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Export */}
              <div className="relative" ref={exportMenuRef}>
                <button
                  onClick={() => setShowExportMenu(v => !v)}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <Download className="h-4 w-4" />Export
                  <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                </button>
                {showExportMenu && (
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-xl shadow-lg w-44 py-1">
                    <button
                      onClick={exportCSV}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Download className="h-4 w-4 text-gray-400" />Export as CSV
                    </button>
                    <button
                      onClick={exportExcel}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Download className="h-4 w-4 text-green-600" />Export as Excel
                    </button>
                  </div>
                )}
              </div>

              {/* Column chooser */}
              <div className="relative" ref={colMenuRef}>
                <button
                  onClick={() => setShowColMenu(v => !v)}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <Columns className="h-4 w-4" />Columns
                </button>
                {showColMenu && (
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-xl shadow-lg w-48 py-1">
                    {ALL_COLUMNS.map(col => (
                      <button
                        key={col.key}
                        onClick={() => toggleColumn(col.key)}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <span>{col.label}</span>
                        {visibleColumns.includes(col.key) && <Check className="h-3.5 w-3.5 text-blue-600" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Active filter chips */}
            {activeFilters.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                {activeFilters.map(({ col: fc, values }) => (
                  <span key={fc} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                    <span className="text-blue-400 mr-0.5">{FILTERABLE_COLS.find(c => c.key === fc)?.label}:</span>
                    {values.map((v, i) => (
                      <span key={v} className="inline-flex items-center gap-0.5">
                        {i > 0 && <span className="text-blue-300 mx-0.5">or</span>}
                        {chipValueLabel(fc, v)}
                        <button onClick={() => toggleFilter(fc, v)} className="hover:text-blue-900 ml-0.5"><X className="h-2.5 w-2.5" /></button>
                      </span>
                    ))}
                  </span>
                ))}
                <button onClick={() => setActiveFilters([])} className="text-xs text-gray-400 hover:text-gray-600 underline">Clear all</button>
              </div>
            )}
          </div>

          {/* Table */}
          {loading || colPrefsLoading ? (
            <div className="flex items-center justify-center py-16 gap-2">
              <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
              <span className="text-sm text-gray-500">Loading issues...</span>
            </div>
          ) : filteredIssues.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <AlertTriangle className="h-9 w-9 text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">{issues.length === 0 ? 'No issues yet. Create your first issue.' : 'No issues match your filters.'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    {visibleColumns.map(key => {
                      const colDef = ALL_COLUMNS.find(c => c.key === key);
                      if (!colDef) return null;
                      const sortable = key === 'id' || key === 'title';
                      return (
                        <th
                          key={key}
                          draggable
                          onDragStart={e => handleColDragStart(e, key)}
                          onDragOver={e => handleColDragOver(e, key)}
                          onDrop={e => handleColDrop(e, key)}
                          onDragEnd={handleColDragEnd}
                          className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap select-none transition-colors
                            ${dragOverCol === key ? 'border-l-2 border-blue-400 bg-blue-50/40' : ''}
                            ${dragCol === key ? 'opacity-40' : ''}
                          `}
                        >
                          <div className="flex items-center gap-1 group">
                            <GripVertical className="h-3.5 w-3.5 text-gray-300 opacity-0 group-hover:opacity-100 cursor-grab shrink-0" />
                            {sortable ? (
                              <button onClick={() => handleSort(key)} className="flex items-center gap-0.5 hover:text-gray-700">
                                {colDef.label}
                                {sortBy === key
                                  ? sortDir === 'asc' ? <ArrowUp className="h-3 w-3 text-blue-600" /> : <ArrowDown className="h-3 w-3 text-blue-600" />
                                  : <ArrowUp className="h-3 w-3 text-gray-300 opacity-0 group-hover:opacity-100" />}
                              </button>
                            ) : (
                              <span>{colDef.label}</span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredIssues.map(issue => (
                    <tr key={issue.id} onClick={() => handleRowClick(issue)} className="hover:bg-gray-50 cursor-pointer transition-colors">
                      {visibleColumns.map(key => {
                        switch (key) {
                          case 'id': return (
                            <td key={key} className="px-4 py-3 whitespace-nowrap text-xs font-mono font-semibold text-blue-600">{issue.issue_id}</td>
                          );
                          case 'title': return (
                            <td key={key} className="px-4 py-3 max-w-xs">
                              <p className="text-sm font-medium text-gray-900 line-clamp-2">{issue.title}</p>
                            </td>
                          );
                          case 'department': return (
                            <td key={key} className="px-4 py-3 whitespace-nowrap">
                              {issue.department
                                ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{issue.department}</span>
                                : <span className="text-gray-400 text-xs">—</span>}
                            </td>
                          );
                          case 'severity': return (
                            <td key={key} className="px-4 py-3 whitespace-nowrap"><SeverityBadge value={issue.classification} /></td>
                          );
                          case 'category': return (
                            <td key={key} className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">{issue.category || <span className="text-gray-400">—</span>}</td>
                          );
                          case 'issue_type': return (
                            <td key={key} className="px-4 py-3 whitespace-nowrap">
                              {(issue.issue_type || 'operating_effectiveness') === 'design'
                                ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">Design</span>
                                : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Operating Effectiveness</span>}
                            </td>
                          );
                          case 'control_id': return (
                            <td key={key} className="px-4 py-3 whitespace-nowrap">
                              {(issue.controls || []).length > 0
                                ? <div className="flex flex-wrap gap-1">{(issue.controls || []).map(c => (
                                    <span key={c.sox_control_id} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-indigo-50 text-indigo-700">{c.control_id}</span>
                                  ))}</div>
                                : <span className="text-gray-400 text-xs">—</span>}
                            </td>
                          );
                          case 'status': return (
                            <td key={key} className="px-4 py-3 whitespace-nowrap"><StatusCell value={issue.status} /></td>
                          );
                          case 'assignee': return (
                            <td key={key} className="px-4 py-3 whitespace-nowrap">
                              {issue.assignee_name
                                ? <p className="text-xs font-medium text-gray-800">{issue.assignee_name}</p>
                                : <span className="text-xs text-gray-400">Unassigned</span>}
                            </td>
                          );
                          case 'period': return (
                            <td key={key} className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">{issue.period || <span className="text-gray-400">—</span>}</td>
                          );
                          case 'recurring': return (
                            <td key={key} className="px-4 py-3 whitespace-nowrap text-xs">
                              {issue.is_repeat
                                ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"><RefreshCw className="h-3 w-3" />Yes</span>
                                : <span className="text-gray-400">No</span>}
                            </td>
                          );
                          default: return null;
                        }
                      })}
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <button
                          onClick={e => { e.stopPropagation(); setDeleteModal({ open: true, issue }); }}
                          className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Create Issue Modal ────────────────────────────────────────────────── */}
      {showCreate && (
        <CreateIssueModal
          onClose={() => setShowCreate(false)}
          onCreated={(created) => {
            setIssues(prev => [created, ...prev]);
            setShowCreate(false);
          }}
          controls={controls}
          tenant={tenant}
          selectedAuditYearId={selectedAuditYearId}
          navigateOnCreate={true}
        />
      )}
      {/* ── Delete Confirm Modal ─────────────────────────────────────────────── */}
      {deleteModal.open && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={() => { setDeleteModal({ open: false, issue: null }); setDeleteError(''); }} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-auto p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Delete Issue</h3>
                  <p className="text-xs text-gray-500">This action cannot be undone.</p>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-6">Delete <span className="font-semibold">{deleteModal.issue?.issue_id}</span>?</p>
              {deleteError && (
                <p className="text-sm text-red-600 mb-3">{deleteError}</p>
              )}
              <div className="flex gap-3 justify-end">
                <button onClick={() => { setDeleteModal({ open: false, issue: null }); setDeleteError(''); }} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleDeleteConfirm} disabled={deleting} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
